import React, { useEffect, useState } from "react";
// import apiClient from "../../../../../services/apiClient";
import AssetGroupTable from "../AssetGroupTable/AssetGroupTable";
import AssetGroupForm from "../AssetGroupForm/AssetGroupForm";
import ErrorDialog from "../../../ErrorDialog/ErrorDialog";
import Dialog from "../../../FormDialog/Dialog";
import { getUserInfo } from "../../../../../services/authService-sighup";
import axios from "axios";
import { getHeaders } from "../../../../ApiHeaders/apiHeadersEntitlements";
import { getHeaders as getAssetHeaders } from "../../../../ApiHeaders/apiHeaders";
import apiClientAdmin from "../../../../../services/apiClientAdmin";
import LoadingStyle from "../../../../Loading/Loading";
import { SplitDivider, useResizableSplit } from "../../../../SplitPane/ResizableSplit";
import { isAmpEntitlementDomain } from "../../../constants/entitlementDomains";

const apiUrl = process.env.REACT_APP_ENTITLEMENTS_API;

interface AssetGroupSetupProps {
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  canDelete?: boolean;
}

const AssetGroupSetup: React.FC<AssetGroupSetupProps> = ({
  registerTabSwitchGuard,
  canDelete = false,
}) => {
  const [assetGroups, setAssetGroups] = useState<any[]>([]);
  const [selectedAssetGroup, setSelectedAssetGroup] = useState<any | null>(
    null
  );
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [error, setError] = useState<{ title: string; details: string } | null>(
    null
  );
  const [user, setUser] = useState<{ fullName: string; email: string } | null>(
    null
  );
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [dialog, setDialog] = useState<{
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    onConfirm: () => {},
  });

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });
  };

  useEffect(() => {
    const fetchUserInfo = () => {
      const userInfo = getUserInfo();
      if (userInfo) {
        setUser({
          fullName: userInfo.fullName || "Unknown User",
          email: userInfo.email || "",
        });
      }
    };
    fetchUserInfo();
  }, []);

  const createdBy = user ? `${user.email || user.fullName}` : "Unknown User";
  const modifiedBy = user ? `${user.email || user.fullName}` : "Unknown User";

  useEffect(() => {
    if (registerTabSwitchGuard) {
      registerTabSwitchGuard(() => {
        return new Promise<boolean>((resolve) => {
          if (isFormDirty || isCreateMode) {
            setDialog({
              open: true,
              title: "Unsaved Changes",
              message:
                "You have unsaved changes. Are you sure you want to discard them?",
              confirmButtonText: "Yes",
              cancelButtonText: "No",
              onConfirm: () => {
                setIsFormDirty(false);
                setIsCreateMode(false);
                closeDialog();
                resolve(true);
              },
              onCancel: () => {
                closeDialog();
                resolve(false);
              },
            });
          } else {
            resolve(true);
          }
        });
      });
    }
  }, [isFormDirty, isCreateMode]);

  const handleApiError = (err: any, fallbackTitle: string) => {
    let title = fallbackTitle;
    let details = "An unknown error occurred";

    const statusCode = err.response?.status;
    const errorResponse = err.response?.data?.error;

    const backendMessage =
      errorResponse?.error?.message || errorResponse?.message;

    switch (statusCode) {
      case 400:
        title = "Bad Request";
        details = backendMessage || "The data you submitted is invalid.";
        break;
      case 401:
        title = "Acces Denied";
        details =
          "You're not authorized to access this content. Please log in or check your access permissions.";
        break;
      case 403:
        title = "Access Denied";
        details = "You need permission to access this site.";
        break;
      case 404:
        title = "Not Found";
        details =
          backendMessage || "The requested resource could not be found.";
        break;
      case 500:
        title = "Internal Server Error";
        details =
          backendMessage ||
          "Server error while fetching data. Please try again later.";
        break;
      default:
        if (
          err.message?.includes("Network Error") ||
          err.message?.includes("timeout")
        ) {
          title = "Network Error";
          details =
            "Unable to connect to the server. Check your internet connection.";
        } else {
          title = fallbackTitle;
          details =
            backendMessage || err.message || "An unexpected error occurred.";
        }
        break;
    }

    setError({ title, details });
    // datadogRum.addError(err, { title, details });
  };

  // const fetchAssetGroups = async () => {
  //   const headers = await getHeaders();
  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_ENTITLEMENTS_API}/asset-group-mappings/`,
  //       { headers }
  //     );
  //     if (!response.ok) {
  //       throw new Error("Failed to fetch asset groups");
  //     }
  //     const data = await response.json();
  //     if (data.response?.data) {
  //       setAssetGroups(data.response.data);
  //     }
  //   } catch (err) {}
  // };
  const fetchAssetGroups = async () => {
    setLoading(true);
    const headers = await getHeaders();
    const assetHeaders = await getAssetHeaders();
  
    try {
      // ─────────────────────────────────────
      // 1️⃣ Fetch collections (groups)
      // ─────────────────────────────────────
      const collectionRes = await fetch(
        `${process.env.REACT_APP_ENTITLEMENTS_API}/collection?enabled_only=false`,
        { headers }
      );
  
      if (!collectionRes.ok) {
        throw new Error("Failed to fetch collections");
      }
  
      const collectionJson = await collectionRes.json();
      const collections = (collectionJson.response?.data || []).filter(
        (c: any) => isAmpEntitlementDomain(c.domain)
      );
  
      // ─────────────────────────────────────
      // 2️⃣ Fetch collection mappings
      // ─────────────────────────────────────
      const mappingRes = await fetch(
        `${process.env.REACT_APP_ENTITLEMENTS_API}/collection-mapping`,
        { headers }
      );
  
      if (!mappingRes.ok) {
        throw new Error("Failed to fetch collection mappings");
      }
  
      const mappingJson = await mappingRes.json();
      const mappings = mappingJson.response?.data || [];
  
      // ─────────────────────────────────────
      // 3️⃣ Fetch ALL assets (paginated)
      // ─────────────────────────────────────
      let allAssets: any[] = [];
      let page = 1;
      const pageSize = 200;
      let hasMore = true;
  
      while (hasMore) {
        const response = await apiClientAdmin.get(
          `/assets/?page=${page}&page_size=${pageSize}`,
          { headers: assetHeaders }
        );
  
        const fetchedAssets = response.data?.response?.data || [];
  
        allAssets = [...allAssets, ...fetchedAssets];
        hasMore = fetchedAssets.length === pageSize;
        page += 1;
      }
  
      // ─────────────────────────────────────
      // 4️⃣ Fetch companies (SAME logic as dropdown)
      // ─────────────────────────────────────
      const companyResponse = await apiClientAdmin.get(
        "/company/",
        { headers: assetHeaders }
      );
  
      const companies =
        companyResponse.data?.response?.data || [];
  
      // ─────────────────────────────────────
      // 5️⃣ Lookup maps (FAST)
      // ─────────────────────────────────────
  
      // Asset lookup
      const assetMap: Record<number, any> = {};
      allAssets.forEach((asset: any) => {
        assetMap[asset.asset_id] = asset;
      });
  
      // Company lookup
      const companyMap: Record<number, string> = {};
      companies.forEach((company: any) => {
        companyMap[company.company_id] =
          company.company_name;
      });
  
      // ─────────────────────────────────────
      // 6️⃣ Build grouped structure
      // ─────────────────────────────────────
      const grouped: Record<number, any> = {};
  
      collections.forEach((collection: any) => {
        grouped[collection.collection_id] = {
          asset_group_id: collection.collection_id,
          asset_group_name: collection.collection_name,
          description: collection.description,
          domain: collection.domain,
          company_id: collection.company_id,
          company_name:
            companyMap[collection.company_id] || "",
          enabled: collection.enabled,
          sys_created_by: collection.sys_created_by,
          sys_modified_by: collection.sys_modified_by,
          sys_created_date: collection.sys_created_date,
          sys_modified_date: collection.sys_modified_date,
          assets: [],
        };
      });
  
      // ─────────────────────────────────────
      // 7️⃣ Attach assets to groups
      // ─────────────────────────────────────
      mappings.forEach((mapping: any) => {
        const { collection_id, object_id } = mapping;
  
        const asset = assetMap[object_id];
  
        if (grouped[collection_id] && asset) {
          grouped[collection_id].assets.push({
            asset_id: asset.asset_id,
            asset_name: asset.asset_name,
          });
        }
      });
  
      // ─────────────────────────────────────
      // 8️⃣ Update UI
      // ─────────────────────────────────────
      setAssetGroups(Object.values(grouped));
  
    } catch (err: any) {
      console.error("Error fetching asset groups:", err);
      handleApiError(err, "Failed to Fetch Asset Groups");
    } finally {
      setLoading(false);
    }
  };
  
  
  

  useEffect(() => {
    fetchAssetGroups();
  }, []);

  // const handleCreateAssetGroup = async (newAssetGroup: any) => {
  //   const baseHeaders = await getHeaders();

  //   const headers = {
  //     "Content-Type": "application/json",
  //     ...baseHeaders,
  //   };

  //   try {
  //     const payload = {
  //       ...newAssetGroup,
  //       sys_created_by: createdBy,
  //       sys_modified_by: modifiedBy,
  //     };

  //     const response = await fetch(
  //       `${process.env.REACT_APP_ENTITLEMENTS_API}/asset-group-mappings/`,
  //       {
  //         method: "POST",
  //         headers,
  //         body: JSON.stringify(payload),
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error("Failed to create asset group");
  //     }

  //     const createdAssetGroup = await response.json();
  //     await fetchAssetGroups();

  //     setDialog({
  //       open: true,
  //       title: "Success",
  //       message: `Asset Group "${newAssetGroup.asset_group_name}" successfully created.`,
  //       confirmButtonText: "Close",
  //       onConfirm: closeDialog,
  //     });
  //   } catch (err) {
  //     handleApiError(err, "Failed to Create Asset Group");
  //   }
  // };
  const handleCreateAssetGroup = async (newAssetGroup: any) => {
    const baseHeaders = await getHeaders();
    const assetHeaders = await getAssetHeaders();
  
    const headers = {
      "Content-Type": "application/json",
      ...baseHeaders,
    };
  
    try {
      // ─────────────────────────────────────
      // STEP 1️⃣ Fetch assets (to map name → id)
      // ─────────────────────────────────────
      let allAssets: any[] = [];
      let page = 1;
      const pageSize = 200;
      let hasMore = true;
  
      while (hasMore) {
        const response = await apiClientAdmin.get(
          `/assets/?page=${page}&page_size=${pageSize}`,
          { headers: assetHeaders }
        );
  
        const fetchedAssets = response.data?.response?.data || [];
        allAssets = [...allAssets, ...fetchedAssets];
  
        hasMore = fetchedAssets.length === pageSize;
        page += 1;
      }
  
      const assetMap: Record<string, number> = {};
      allAssets.forEach((a: any) => {
        assetMap[a.asset_name] = a.asset_id;
      });
  
      // ─────────────────────────────────────
      // STEP 2️⃣ Create Collection
      // ─────────────────────────────────────
      const collectionPayload = {
        collection_name: newAssetGroup.asset_group_name,
        domain: newAssetGroup.domain,
        description: newAssetGroup.description || "",
        enabled: "Y",
        company_id: newAssetGroup.company_id || 0,
        sys_created_by: createdBy,
        sys_modified_by: modifiedBy,
      };
  
      const collectionResponse = await fetch(
        `${process.env.REACT_APP_ENTITLEMENTS_API}/collection`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(collectionPayload),
        }
      );
  
      if (!collectionResponse.ok) {
        throw new Error("Failed to create collection");
      }
  
      const createdCollection = await collectionResponse.json();
  
      const collectionId =
        createdCollection.response?.data?.collection_id;
  
      // ─────────────────────────────────────
      // STEP 3️⃣ Create mappings (loop assets)
      // ─────────────────────────────────────
      for (const assetName of newAssetGroup.asset_names) {
        const assetId = assetMap[assetName];
  
        if (!assetId) continue;
  
        const mappingPayload = {
          collection_id: collectionId,
          object_id: assetId,
          description: "",
          enabled: "Y",
          sys_created_by: createdBy,
          sys_modified_by: modifiedBy,
        };
  
        await fetch(
          `${process.env.REACT_APP_ENTITLEMENTS_API}/collection-mapping`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(mappingPayload),
          }
        );
      }
  
      // ─────────────────────────────────────
      // STEP 4️⃣ Refresh UI
      // ─────────────────────────────────────
      await fetchAssetGroups();
  
      setDialog({
        open: true,
        title: "Success",
        message: `Asset Group "${newAssetGroup.asset_group_name}" successfully created.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
  
    } catch (err: any) {
      handleApiError(err, "Failed to Create Asset Group");
    }
  };
  

  // const handleSaveAssetGroup = async (
  //   assetGroupId: any,
  //   updatedAssetGroup: any
  // ) => {
  //   const headers = await getHeaders();
  //   try {
  //     const original = assetGroups.find(
  //       (g) => g.asset_group_id === assetGroupId
  //     );
  //     const assetGroupNameChanged =
  //       original?.asset_group_name !== updatedAssetGroup.asset_group_name;

  //     //Update asset_group_name if changed
  //     if (assetGroupNameChanged) {
  //       const namePayload = {
  //         asset_group_name: updatedAssetGroup.asset_group_name,
  //         description: original?.description || "",
  //         enabled: original?.enabled || "Y",
  //         sys_created_by:
  //           original?.sys_created_by || updatedAssetGroup.sys_created_by,
  //         sys_modified_by: updatedAssetGroup.sys_modified_by,
  //       };

  //       await axios.put(
  //         `${process.env.REACT_APP_ENTITLEMENTS_API}/asset-group/${assetGroupId}`,
  //         namePayload,
  //         { headers }
  //       );
  //     }

  //     // Update asset-group-mappings
  //     const response = await axios.put(
  //       `${process.env.REACT_APP_ENTITLEMENTS_API}/asset-group-mappings/${assetGroupId}`,
  //       updatedAssetGroup,
  //       { headers }
  //     );
  //     if (response.status === 200) {
  //       setAssetGroups((prev) =>
  //         prev.map((group) =>
  //           group.asset_group_id === updatedAssetGroup.asset_group_id
  //             ? updatedAssetGroup
  //             : group
  //         )
  //       );
  //       await fetchAssetGroups();

  //       setDialog({
  //         open: true,
  //         title: "Success",
  //         message: `Asset Group "${updatedAssetGroup.asset_group_name}" successfully updated.`,
  //         confirmButtonText: "Close",
  //         onConfirm: closeDialog,
  //       });
  //     }
  //   } catch (err) {
  //     handleApiError(err, "Failed to Update Asset Group");
  //   }
  // };

  const handleSaveAssetGroup = async (
    assetGroupId: any,
    updatedAssetGroup: any
  ) => {
    const headers = await getHeaders();
    const assetHeaders = await getAssetHeaders();
  
    try {
      // ─────────────────────────────────────
      // STEP 1️⃣ Get original group
      // ─────────────────────────────────────
      const original = assetGroups.find(
        (g) => g.asset_group_id === assetGroupId
      );
  
      if (!original) return;
  
      // ─────────────────────────────────────
      // STEP 2️⃣ Fetch assets (name -> id)
      // ─────────────────────────────────────
      let allAssets: any[] = [];
      let page = 1;
      const pageSize = 200;
      let hasMore = true;
  
      while (hasMore) {
        const response = await apiClientAdmin.get(
          `/assets/?page=${page}&page_size=${pageSize}`,
          { headers: assetHeaders }
        );
  
        const fetchedAssets = response.data?.response?.data || [];
        allAssets = [...allAssets, ...fetchedAssets];
  
        hasMore = fetchedAssets.length === pageSize;
        page += 1;
      }
  
      const assetMap: Record<string, number> = {};
      allAssets.forEach((a: any) => {
        assetMap[a.asset_name] = a.asset_id;
      });
  
      // ─────────────────────────────────────
      // STEP 3️⃣ Update Collection
      // ─────────────────────────────────────
      const collectionPayload = {
        collection_name: updatedAssetGroup.asset_group_name,
        domain: updatedAssetGroup.domain,      
        description: updatedAssetGroup.description || "",
        enabled: "Y",
        company_id: updatedAssetGroup.company_id || 0,
        sys_created_by: original.sys_created_by,
        sys_modified_by: updatedAssetGroup.sys_modified_by,
      };
      
  
      await fetch(
        `${process.env.REACT_APP_ENTITLEMENTS_API}/collection/${assetGroupId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...headers,
          },
          body: JSON.stringify(collectionPayload),
        }
      );
  
      // ─────────────────────────────────────
      // STEP 4️⃣ Compare assets
      // ─────────────────────────────────────
      const originalNames =
        original.assets?.map((a: any) => a.asset_name) || [];
  
      const updatedNames = updatedAssetGroup.asset_names || [];
  
      const addedAssets = updatedNames.filter(
        (n: string) => !originalNames.includes(n)
      );
  
      const removedAssets = originalNames.filter(
        (n: string) => !updatedNames.includes(n)
      );
  
      // ─────────────────────────────────────
      // STEP 5️⃣ ADD new mappings
      // ─────────────────────────────────────
      for (const assetName of addedAssets) {
        const assetId = assetMap[assetName];
        if (!assetId) continue;
  
        const payload = {
          collection_id: assetGroupId,
          object_id: assetId,
          description: "",
          enabled: "Y",
          sys_created_by: updatedAssetGroup.sys_modified_by,
          sys_modified_by: updatedAssetGroup.sys_modified_by,
        };
  
        await fetch(
          `${process.env.REACT_APP_ENTITLEMENTS_API}/collection-mapping`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...headers,
            },
            body: JSON.stringify(payload),
          }
        );
      }
  
      // ─────────────────────────────────────
      // STEP 6️⃣ REMOVE deleted mappings
      // ─────────────────────────────────────
      for (const assetName of removedAssets) {
        const assetId = assetMap[assetName];
        if (!assetId) continue;
  
        await fetch(
          `${process.env.REACT_APP_ENTITLEMENTS_API}/collection-mapping/collection/${assetGroupId}/object/${assetId}`,
          {
            method: "DELETE",
            headers,
          }
        );
      }
  
      // ─────────────────────────────────────
      // STEP 7️⃣ Refresh UI
      // ─────────────────────────────────────
      await fetchAssetGroups();
  
      setDialog({
        open: true,
        title: "Success",
        message: `Asset Group "${updatedAssetGroup.asset_group_name}" successfully updated.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
  
    } catch (err) {
      handleApiError(err, "Failed to Update Asset Group");
    }
  };

  const handleDeleteAssetGroup = async (assetGroupId: number) => {
    const headers = await getHeaders();
  
    try {
      // STEP 1 — Fetch object_ids for this collection
      const mappingRes = await fetch(
        `${process.env.REACT_APP_ENTITLEMENTS_API}/collection-mapping/collection/${assetGroupId}/objects`,
        { headers }
      );
  
      if (!mappingRes.ok) {
        throw new Error("Failed to fetch collection mappings");
      }
  
      const mappingJson = await mappingRes.json();
  
      const objectIds =
        mappingJson?.response?.data?.object_ids || [];
  
      // STEP 2 — Delete each mapping
      await Promise.all(
        objectIds.map((objectId: number) =>
          fetch(
            `${process.env.REACT_APP_ENTITLEMENTS_API}/collection-mapping/collection/${assetGroupId}/object/${objectId}`,
            {
              method: "DELETE",
              headers,
            }
          )
        )
      );
  
      // STEP 3 — Delete the collection (asset group)
      const deleteCollection = await fetch(
        `${process.env.REACT_APP_ENTITLEMENTS_API}/collection/${assetGroupId}`,
        {
          method: "DELETE",
          headers,
        }
      );
  
      if (!deleteCollection.ok) {
        throw new Error("Failed to delete collection");
      }
  
      // STEP 4 — Refresh UI
      await fetchAssetGroups();
  
      setSelectedAssetGroup(null);
      setSelectedRow(null);
      setIsFormDirty(false);
      setIsCreateMode(false);
  
      setDialog({
        open: true,
        title: "Deleted",
        message: "Asset Group deleted successfully.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
  
    } catch (err) {
      handleApiError(err, "Failed to Delete Asset Group");
    }
  };
  

  // const handleRowSelect = (assetGroup: any) => {
  //   setSelectedAssetGroup(assetGroup);
  //   setSelectedRow(assetGroup.asset_group_id);
  // };
  const handleRowSelect = (assetGroup: any) => {
    if (isFormDirty || isCreateMode) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setIsCreateMode(false);
          setSelectedAssetGroup(assetGroup);
          setSelectedRow(assetGroup.asset_group_id);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedAssetGroup(assetGroup);
      setSelectedRow(assetGroup.asset_group_id);
    }
  };

  // const handleClearSelection = () => {
  //   setSelectedRow(null);
  // };
  const handleClearSelection = () => {
    if (isFormDirty) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        // confirmButtonText: "Discard Changes",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          // setSelectedResource(null);
          setSelectedRow(null);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      // setSelectedResource(null);
      setSelectedRow(null);
    }
  };

  const split = useResizableSplit("xl");
  return (
    <div ref={split.containerRef} style={split.containerStyle} className={`flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}>
      {/* <div className="flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white"> */}
      <div className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
            <LoadingStyle />
          </div>
        )}
        <AssetGroupTable
          assetGroups={assetGroups}
          onEditAssetGroup={handleSaveAssetGroup}
          onRowSelect={handleRowSelect}
          onClearSelection={handleClearSelection}
          selectedRow={selectedRow}
        />
      </div>
      {/* <div className="flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white"> */}
      <SplitDivider {...split.dividerProps} />
      <div className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 xl:self-stretch bg-white rounded-xl shadow-md">
        <AssetGroupForm
          onCreateAssetGroup={handleCreateAssetGroup}
          selectedAssetGroup={selectedAssetGroup}
          onSaveChanges={handleSaveAssetGroup}
          onDeleteAssetGroup={handleDeleteAssetGroup}
          onClearSelection={handleClearSelection}
          assetGroups={assetGroups}
          onDirtyChange={setIsFormDirty}
          onModeChange={(mode) => setIsCreateMode(mode === "create")}
          canDelete={canDelete}
        />
      </div>
      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          onConfirm={dialog.onConfirm}
          cancelButtonText={dialog.cancelButtonText}
          onCancel={dialog.onCancel}
          onClose={closeDialog}
        />
      )}
      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
};

export default AssetGroupSetup;
